
/**
 * Photo capture. Primary path: getUserMedia live stream (used by the
 * PhotoCapture component via captureFrame). Fallback path: a file input with
 * capture="environment", which always works on mobile browsers.
 * Capacitor swap: @capacitor/camera.
 */

/**
 * The rear camera.
 *
 * 960 rather than 1280. The fleet carries phones from 2015-2016 (iOS 15.8 is
 * as far as an iPhone 6s or 7 goes), and every extra pixel is work the barcode
 * decoder has to do several times a second on a chip that cannot spare it —
 * enough that Safari was discarding the tab mid-scan. A 960-wide frame reads a
 * hospital label perfectly well.
 */
export async function openRearCameraStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 960 } },
    audio: false,
  });
}

/**
 * The rear camera, for PHOTOGRAPHING A DOCUMENT.
 *
 * Deliberately not the same as the scanner's. 960 pixels was chosen because
 * the barcode decoder has to chew through a frame twenty-five times a second
 * on a phone from 2016, and that is a real constraint -- for the scanner.
 *
 * A manifest photo is taken ONCE. Asking a document to live inside a size
 * picked for a video loop is why the writing on it looks soft: 960 pixels
 * across a sheet of paper is roughly 120 dots per inch, below what it takes to
 * read a printed line back with confidence.
 *
 * NOTHING HERE MAY COST A DRIVER THEIR CAMERA.
 *
 * `ideal` is a preference, not a demand -- by the specification a browser must
 * pick the closest it can manage rather than refuse -- but specifications and
 * a 2016 handset in a hospital basement are different things, and a camera
 * that will not open is a stop that cannot be finished.
 *
 * So the better picture is ATTEMPTED and the known-good stream is the floor.
 * If the request is refused for any reason at all, this falls back to exactly
 * the stream the scanner has been using without trouble for days. The worst
 * case is the photo quality we had yesterday; there is no case where the
 * camera fails to open that would not also have failed yesterday.
 */
export async function openDocumentCameraStream(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1440 },
      },
      audio: false,
    });
  } catch {
    // Any refusal at all -- an unsupported size, a fussy old browser, a
    // camera already held by something else -- drops to the stream that is
    // already proven on every phone in this fleet.
    return openRearCameraStream();
  }
}

export function captureFrame(video: HTMLVideoElement, quality = 0.8): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')!.drawImage(video, 0, 0);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/*
 * No file-input fallback (31 Oct). The phone's own camera app, reached through
 * a file picker, can offer pictures already on the phone. Owner: uploading
 * photos "is unacceptable". Every picture this app takes comes from the live
 * in-app camera.
 */
